/**
 * Shared model display-name builder for AI-transaction records
 * (2026-06-11 backlog item 1: "line up and record ALL model activity to
 * the user accurately").
 *
 * Previously FOUR byte-identical copies of this function lived in
 * orchestrator/index.ts, tools/deepResearch.ts, tools/translate.ts, and
 * tools/videoScript.ts — and they had already drifted from the canonical
 * codename map in src/config/providerCodeNames.ts (e.g. 'Echo Deep
 * Research' here vs 'echo-deep' in the ⓘ popup). This module is now the
 * single recording-time builder; the ladder below is preserved verbatim so
 * existing wallet display strings do not change, and unmatched ids fall
 * through to the canonical `getModelCodeName` map instead of leaking raw
 * vendor model ids into the UI.
 */

import { getPublicSystemName } from '../../config/providerCodeNames';
import type { TokenUsageMetadata } from './types';

/**
 * Derive the wallet/ⓘ-popup provider family from a model id (2026-08-15).
 * The orchestrator previously hard-coded provider:'claude' while its wire
 * model was the default-spawn primary (x-ai/grok-4.6) — expense rows and the
 * provider dot misattributed the spend. Mirrors the server-side
 * deriveProviderFromModelId posture: derive from the ACTUAL id, fall back to
 * the caller's default only when the id carries no provider signal.
 */
export function deriveProviderFromModelId(
  modelId: string | undefined,
  fallback: TokenUsageMetadata['provider'] = 'unknown',
): NonNullable<TokenUsageMetadata['provider']> {
  const id = String(modelId || '').toLowerCase();
  if (!id) return fallback ?? 'unknown';
  if (id.includes('grok') || id.startsWith('x-ai/')) return 'grok';
  if (id.includes('claude') || id.startsWith('anthropic/')) return 'claude';
  if (id.includes('gemini') || id.startsWith('google/')) return 'gemini';
  if (id.includes('sonar') || id.startsWith('perplexity/')) return 'perplexity';
  if (id.startsWith('openai/') || id.startsWith('gpt-') || id.includes('/gpt-')) return 'openai';
  if (id.startsWith('deepseek/') || id.includes('deepseek')) return 'deepseek';
  if (id.startsWith('minimax/') || id.includes('minimax')) return 'minimax';
  if (id.startsWith('qwen/') || id.includes('qwen')) return 'qwen';
  return fallback ?? 'unknown';
}

export function buildModelDisplay(
  tokenUsage: TokenUsageMetadata | undefined,
  taskSuffix: string,
  fallbackDisplay: string,
): string {
  if (!tokenUsage?.model) return fallbackDisplay;
  // 2026-08-13 naming migration: recording-time display names use the
  // VERSIONLESS family vocabulary (getPublicSystemName) — the old ladder
  // here stamped versioned codenames ('Apex-4.8') into persisted records,
  // and the version digits were exactly the leak this layer removes.
  // Historical rows keep their old strings; render paths sanitize them.
  const station = getPublicSystemName(tokenUsage.model);
  const codename = station !== 'system' ? station : fallbackDisplay;
  return taskSuffix ? `${codename} (${taskSuffix})` : codename;
}
